import React, { useState, useEffect } from "react";
import { Layout, Menu, MenuProps } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import {
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  PlusOutlined,
  UnorderedListOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  CreditCardOutlined,
  DollarOutlined,
  BarChartOutlined,
} from "@ant-design/icons";
import { useAuth } from "@/context/AuthContext";
import { MenuItem, AntMenuItem } from "@/types";

const { Sider } = Layout;

interface NavbarProps {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
}

const Navbar: React.FC<NavbarProps> = ({ collapsed, setCollapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, user } = useAuth();
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]); // if opens sub page, find with startWith because sub page is not in the menu

  const getParentKey = (path: string): string | null => {
    const menuItems = getMenuItems();
    for (const item of menuItems) {
      if (item.children) {
        for (const child of item.children) {
          if (child.key === path) {
            return item.key;
          }
        }
      }
    }
    return null;
  };

  const getSelectedKey = (currentPath: string): string | null => {
    const menuItems = getMenuItems();

    // First, check if current path exactly matches a child menu item
    let selectedKey = currentPath;

    // Look through all menu items and their children
    for (const item of menuItems) {
      if (item.children) {
        // Check if current path matches any child exactly
        const matchingChild = item.children.find(
          (child) => child.key === currentPath
        );
        if (matchingChild) {
          selectedKey = currentPath;
          break;
        }
        // Check if current path starts with any child (for sub-routes)
        const matchingChildStart = item.children.find((child) =>
          currentPath.startsWith(child.key)
        );
        if (matchingChildStart) {
          selectedKey = matchingChildStart.key;
          break;
        }
      } else {
        // Check if current path exactly matches a parent menu item
        if (item.key === currentPath) {
          selectedKey = currentPath;
          break;
        }
        // Check if current path starts with a parent menu item (for sub-routes)
        if (currentPath.startsWith(item.key) && item.key !== currentPath) {
          selectedKey = item.key;
          break;
        }
      }
    }

    return selectedKey;
  };

  // Auto-expand menu based on current location
  useEffect(() => {
    const currentPath = location.pathname;

    // Set the selected key
    const selectedKey = getSelectedKey(currentPath);
    if (selectedKey) {
      setSelectedKeys([selectedKey]);
    }

    // Set open keys for parent menus
    if (getParentKey(currentPath)) {
      setOpenKeys([getParentKey(currentPath) || ""]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const getMenuItems = (): MenuItem[] => {
    const baseItems: MenuItem[] = [];

    if (user?.roles.includes("worker")) {
      baseItems.push({
        key: "/expenses",
        label: "Expenses",
        icon: <CreditCardOutlined />,
        children: [
          {
            key: "/expenses/record",
            label: "New Expense",
            icon: <PlusOutlined />,
          },
        ],
      });
    } else if (user?.roles.includes("admin")) {
      baseItems.push(
        // Overview
        {
          key: "/overview",
          icon: <BarChartOutlined />,
          label: "Overview",
        },
        // Primary Operations (Daily Use)
        {
          key: "/sales",
          label: "Sales",
          icon: <DollarOutlined />,
          // children: [
          // {
          //   key: "/sales/overview",
          //   label: "Overview",
          //   icon: <UnorderedListOutlined />,
          // },
          // {
          //   key: "/sales/new",
          //   label: "New Sale",
          //   icon: <PlusOutlined />,
          // },
          // ],
        },
        // Product Management
        // {
        //   key: "/items",
        //   label: "Items",
        //   icon: <ShoppingOutlined />,
        //   children: [
        //     {
        //       key: "/items/overview",
        //       label: "Overview",
        //       icon: <UnorderedListOutlined />,
        //     },
        //     {
        //       key: "/items/new",
        //       label: "New Item",
        //       icon: <PlusOutlined />,
        //     },
        //   ],
        // },
        // {
        //   key: "/recipes",
        //   label: "Recipes",
        //   icon: <BookOutlined />,
        //   children: [
        //     {
        //       key: "/recipes/overview",
        //       label: "Overview",
        //       icon: <UnorderedListOutlined />,
        //     },
        //     {
        //       key: "/recipes/new",
        //       label: "New Recipe",
        //       icon: <PlusOutlined />,
        //     },
        //   ],
        // },
        // {
        //   key: "/items",
        //   label: "Items",
        //   icon: <ShoppingOutlined />,
        //   children: [
        //     {
        //       key: "/items/overview",
        //       label: "Overview",
        //       icon: <UnorderedListOutlined />,
        //     },
        //     {
        //       key: "/items/new",
        //       label: "New Item",
        //       icon: <PlusOutlined />,
        //     },
        //   ],
        // },
        // {
        //   key: "/recipes",
        //   label: "Recipes",
        //   icon: <BookOutlined />,
        //   children: [
        //     {
        //       key: "/recipes/overview",
        //       label: "Overview",
        //       icon: <UnorderedListOutlined />,
        //     },
        //     {
        //       key: "/recipes/new",
        //       label: "New Recipe",
        //       icon: <PlusOutlined />,
        //     },
        //   ],
        // },
        // Inventory Management
        // {
        //   key: "/inventory",
        //   label: "Inventory",
        //   icon: <InboxOutlined />,
        //   children: [
        //     {
        //       key: "/inventory/overview",
        //       label: "Overview",
        //       icon: <UnorderedListOutlined />,
        //     },
        //     {
        //       key: "/inventory/add",
        //       label: "New Inventory",
        //       icon: <PlusOutlined />,
        //     },
        //     {
        //       key: "/inventory/transaction",
        //       label: "Transaction",
        //       icon: <SyncOutlined />,
        //     },
        //   ],
        // },
        // {
        //   key: "/inventory",
        //   label: "Inventory",
        //   icon: <InboxOutlined />,
        //   children: [
        //     {
        //       key: "/inventory/overview",
        //       label: "Overview",
        //       icon: <UnorderedListOutlined />,
        //     },
        //     {
        //       key: "/inventory/add",
        //       label: "New Inventory",
        //       icon: <PlusOutlined />,
        //     },
        //     {
        //       key: "/inventory/transaction",
        //       label: "Transaction",
        //       icon: <SyncOutlined />,
        //     },
        //   ],
        // },
        // Financial Management
        {
          key: "/expenses",
          label: "Expenses",
          icon: <CreditCardOutlined />,
          children: [
            {
              key: "/expenses/details",
              label: "Details",
              icon: <UnorderedListOutlined />,
            },
            {
              key: "/expenses/record",
              label: "New Expense",
              icon: <PlusOutlined />,
            },
            {
              key: "/expenses/manage",
              label: "Manage",
              icon: <SettingOutlined />,
            },
          ],
        },
        // Analytics & Reports
        // {
        //   key: "/analytics",
        //   label: "Reports & AI",
        //   icon: <BarChartOutlined />,
        //   children: [
        //     {
        //       key: "/analytics/overview",
        //       label: "Overview",
        //       icon: <BarChartOutlined />,
        //     },
        //     {
        //       key: "/analysis",
        //       label: "AI Analysis",
        //       icon: <LineChartOutlined />,
        //     },
        //   ],
        // },
        // {
        //   key: "/analytics",
        //   label: "Reports & AI",
        //   icon: <BarChartOutlined />,
        //   children: [
        //     {
        //       key: "/analytics/overview",
        //       label: "Overview",
        //       icon: <BarChartOutlined />,
        //     },
        //     {
        //       key: "/analysis",
        //       label: "AI Analysis",
        //       icon: <LineChartOutlined />,
        //     },
        //   ],
        // },
        // Administration
        {
          key: "/users",
          icon: <UserOutlined />,
          label: "Users",
        }
        // {
        //   key: "/settings",
        //   icon: <SettingOutlined />,
        //   label: "Settings",
        // }
        // {
        //   key: "/settings",
        //   icon: <SettingOutlined />,
        //   label: "Settings",
        // }
      );
    }

    return baseItems;
  };

  const handleMenuClick: MenuProps["onClick"] = (e) => {
    navigate(e.key);
  };

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const handleOpenChange = (keys: string[]) => {
    if (collapsed) {
      return;
    }

    // Only allow one parent menu to be open at a time (accordion style)
    const latestOpenKey = keys.find((key) => openKeys.indexOf(key) === -1);
    if (latestOpenKey) {
      setOpenKeys([latestOpenKey]);
    } else {
      setOpenKeys([]);
    }
  };

  return (
    <Sider
      trigger={null}
      collapsible
      collapsed={collapsed}
      style={{
        height: "100vh",
        position: "fixed",
        left: 0,
        top: 0,
        bottom: 0,
        zIndex: 1000,
      }}
    >
      <style>
        {`
          .sidebar-scroll::-webkit-scrollbar {
            width: 6px;
          }
          .sidebar-scroll::-webkit-scrollbar-track {
            background: #001529;
          }
          .sidebar-scroll::-webkit-scrollbar-thumb {
            background: #666;
            border-radius: 3px;
          }
          .sidebar-scroll::-webkit-scrollbar-thumb:hover {
            background: #888;
          }
        `}
      </style>

      <div
        style={{
          height: 64,
          display: "flex",
          alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          padding: collapsed ? "0" : "0 16px",
          color: "white",
          backgroundColor: "#001529",
          borderBottom: "1px solid #303030",
        }}
      >
        {!collapsed && (
          <span
            style={{ color: "white", fontSize: "18px", fontWeight: "bold" }}
          >
            Kraken
          </span>
        )}
        {React.createElement(
          collapsed ? MenuUnfoldOutlined : MenuFoldOutlined,
          {
            style: { color: "white", fontSize: "16px", cursor: "pointer" },
            onClick: () => setCollapsed(!collapsed),
          }
        )}
      </div>

      <div
        style={{
          height: "calc(100dvh - 64px)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          className="sidebar-scroll"
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            scrollbarWidth: "thin",
            scrollbarColor: "#666 #001529",
          }}
        >
          <Menu
            theme="dark"
            mode="inline"
            selectedKeys={selectedKeys}
            openKeys={collapsed ? undefined : openKeys}
            onOpenChange={handleOpenChange}
            items={getMenuItems() as AntMenuItem[]}
            onClick={handleMenuClick}
            style={{ borderRight: 0 }}
          />
        </div>

        {/* Logout button at the bottom */}
        <div
          style={{
            padding: "8px 0",
          }}
        >
          <Menu
            theme="dark"
            mode="inline"
            style={{
              borderRight: 0,
            }}
            items={[
              {
                key: "logout",
                icon: <LogoutOutlined />,
                label: "Logout",
                onClick: handleLogout,
              },
            ]}
          />
        </div>
      </div>
    </Sider>
  );
};

export default Navbar;
